import { useCallback, useEffect, useRef, useState } from 'react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'

export type TiunSessionState = {
  isTiunSessionActive: boolean | null
}

export type GetCurrentTiunSessionState = (
  queryClient: QueryClient,
) => TiunSessionState

const getCurrentTiunSessionState: GetCurrentTiunSessionState = (queryClient) =>
  queryClient.getQueryData<TiunSessionState>(['tiun-session-state']) ?? {
    isTiunSessionActive: null,
  }

const useTiunSessionState = () => {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()

  const [tiunSessionState, setTiunSessionState] = useState<TiunSessionState>(
    () => getCurrentTiunSessionState(queryClient),
  )

  const unsubscribeFnRef = useRef<() => void>(undefined)

  const updateTiunSessionState = useCallback(
    (args: any) => {
      if (
        args?.action?.type === 'invalidate' &&
        args?.query?.queryKey[0] === 'tiun-session-state'
      ) {
        setTiunSessionState(getCurrentTiunSessionState(queryClient))
      }
    },
    [queryClient],
  )

  useEffect(() => {
    unsubscribeFnRef.current = queryCache.subscribe(updateTiunSessionState)
    setTiunSessionState(getCurrentTiunSessionState(queryClient))

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [updateTiunSessionState, queryCache, queryClient])

  return tiunSessionState
}

export default useTiunSessionState
